import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info, XCircle } from "lucide-react";

export type ToastTone = "error" | "info" | "success";

export interface ToastState {
  message: string;
  title: string;
  tone: ToastTone;
}

interface ToastViewportProps {
  onDismiss: () => void;
  toast: ToastState | null;
}

const toneConfig = {
  success: {
    border: "border-emerald-200",
    icon: CheckCircle2,
    iconClassName: "text-emerald-600",
    titleClassName: "text-emerald-900"
  },
  info: {
    border: "border-cyan-200",
    icon: Info,
    iconClassName: "text-royal",
    titleClassName: "text-ink"
  },
  error: {
    border: "border-rose-200",
    icon: XCircle,
    iconClassName: "text-rose-600",
    titleClassName: "text-rose-900"
  }
} satisfies Record<
  ToastTone,
  {
    border: string;
    icon: typeof AlertCircle;
    iconClassName: string;
    titleClassName: string;
  }
>;

export function ToastViewport({ onDismiss, toast }: ToastViewportProps) {
  useEffect(() => {
    if (!toast) {
      return;
    }

    const timeout = window.setTimeout(onDismiss, 3600);

    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast]);

  const Icon = toast ? toneConfig[toast.tone].icon : null;

  return (
    <div className="pointer-events-none fixed inset-x-4 top-20 z-[75] flex justify-end" data-testid="toast-viewport">
      <AnimatePresence>
        {toast && Icon ? (
          <motion.div
            animate={{ opacity: 1, x: 0, y: 0 }}
            className={`pointer-events-auto w-full max-w-sm rounded-[24px] border bg-white/96 p-5 shadow-float backdrop-blur-xl ${toneConfig[toast.tone].border}`}
            data-testid="toast"
            exit={{ opacity: 0, x: 18, y: -6 }}
            initial={{ opacity: 0, x: 24, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-cloud">
                <Icon className={`h-5 w-5 ${toneConfig[toast.tone].iconClassName}`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${toneConfig[toast.tone].titleClassName}`} data-testid="toast-title">
                  {toast.title}
                </p>
                <p className="mt-1 text-sm leading-6 text-slate" data-testid="toast-message">
                  {toast.message}
                </p>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
